import { computed, onScopeDispose, ref, toValue, watch, type MaybeRefOrGetter } from 'vue'
import { reviewGoalStream, type AuditResult, type CriteriaItem } from '@/content-review/api'
import { useContentReviewStore } from '@/content-review/content-review.store'
import { useContentReview } from '@/content-review/composables/useContentReview'
import { calculateNormalizedScores } from '@/content-review/utils/calculateNormalizedScores'
import { useVisibilityStore } from '@/visibility/visibility.store'
import type { AnalyticsData, AnalyticsTracker } from '@/tracking/analytics/analyticsTracker'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { GOAL_AUTO_REVIEW_EVENT_NAMES } from './event_names'
import {
  isCriterionForGoal,
  resultsForSection,
  sortByCriteriaOrder,
  type GoalReviewSection,
} from './goal_review_criteria'
import { shouldShowGoalReview } from './should_show_goal_review'

export interface GoalReviewContent {
  uuid: string
  goalText: string
  objectiveTexts: string[]
  grades: string[]
  subjects: string[]
  isCustomized: boolean
}

export interface GoalAutoReviewOptions {
  goal: MaybeRefOrGetter<GoalReviewContent>
  isSavedGoal?: boolean
  /** The page's own tracker, when the page calls this in the same setup that provides its event context. */
  analytics?: AnalyticsTracker
}

export interface ReviewPaneProps {
  title: string
  loadingText: string
  results: AuditResult[]
  isLoading: boolean
  error: string | null
  expectedCount: number
}

const REVIEW_ERROR = 'Unable to load goal review scores. Try again in a moment.'

const PANE_COPY: Record<GoalReviewSection, { title: string; loadingText: string }> = {
  goals: { title: 'Goal Review', loadingText: 'Reviewing Goal...' },
  objectives: { title: 'Objectives Review', loadingText: 'Reviewing Objectives...' },
}

function toScoreSummary({ score, max_score, audit_title, section }: AuditResult) {
  return { score, max_score, criteria_title: audit_title, section }
}

/** The name of the criteria set goals are reviewed against, empty for Goalbook's default set. */
export function useGoalCriteriaSetName() {
  const contentReviewStore = useContentReviewStore()
  const visibilityStore = useVisibilityStore()
  return computed(() =>
    contentReviewStore.criteriaSetNameForContentType(
      contentReviewStore.reviewProfileId,
      'goals',
      visibilityStore.betaVisibility
    )
  )
}

export function useGoalAutoReview({ goal, isSavedGoal = false, analytics = getAnalyticsTracker() }: GoalAutoReviewOptions) {
  const contentReviewStore = useContentReviewStore()
  const visibilityStore = useVisibilityStore()
  const { isReviewing, reviewResults, reviewError, startReview, cancelReview, clearResults } =
    useContentReview({ errorMessage: REVIEW_ERROR })

  const isModalOpen = ref(false)
  const isDismissed = ref(false)
  const hasCompletedReview = ref(false)

  if (!contentReviewStore.profiles.length) {
    contentReviewStore.fetchProfiles()
  }

  const criteriaFor = (section: GoalReviewSection): CriteriaItem[] =>
    contentReviewStore.criteriaForProfile(
      contentReviewStore.reviewProfileId,
      section,
      visibilityStore.betaVisibility
    )

  const criteriaByType = computed(() => ({
    goals: criteriaFor('goals'),
    objectives: criteriaFor('objectives'),
  }))

  const criteriaSetName = useGoalCriteriaSetName()

  const isCustomCriteriaSet = computed(() =>
    contentReviewStore.isCustomCriteriaSet(contentReviewStore.reviewProfileId, visibilityStore.betaVisibility)
  )

  function isCriterionVisible(criterion: CriteriaItem): boolean {
    const { grades, subjects } = toValue(goal)
    return isCriterionForGoal(criterion, { grades, subjects })
  }

  const visibleCriteria = computed(() => ({
    goals: criteriaByType.value.goals.filter(isCriterionVisible),
    objectives: criteriaByType.value.objectives.filter(isCriterionVisible),
  }))

  const hasObjectivesCriteria = computed(() => criteriaByType.value.objectives.length > 0)
  const hasReviewProfile = computed(() => Boolean(contentReviewStore.reviewProfileId))
  const hasApplicableCriteria = computed(
    () => visibleCriteria.value.goals.length > 0 || visibleCriteria.value.objectives.length > 0
  )
  const hasReviewCriteria = computed(() => hasReviewProfile.value && hasApplicableCriteria.value)

  const isReviewingObjectives = computed(() => visibleCriteria.value.objectives.length > 0)
  const reviewButtonText = computed(() => (isReviewingObjectives.value ? 'Review Goal & Objectives' : 'Review Goal'))
  const reviewDescriptionLabel = computed(() => (isReviewingObjectives.value ? 'goal and objectives' : 'goal'))

  const isReviewAvailable = computed(() => {
    const { uuid, isCustomized } = toValue(goal)
    return shouldShowGoalReview({
      criteriaSetName: criteriaSetName.value,
      isGoalCustomized: isCustomized,
      isAiFeatureVisible: visibilityStore.aiFeatureVisibility,
      goalUuid: uuid,
      hasReviewCriteria: hasReviewCriteria.value,
    })
  })

  function isAwaitingResults(received: number, expected: number): boolean {
    if (!isReviewing.value) return false
    if (expected > 0) return received < expected
    return received === 0
  }

  function paneProps(section: GoalReviewSection): ReviewPaneProps {
    const criteria = visibleCriteria.value[section]
    const results = sortByCriteriaOrder(resultsForSection(reviewResults.value, section), criteria)
    return {
      ...PANE_COPY[section],
      results,
      isLoading: isAwaitingResults(results.length, criteria.length),
      error: reviewError.value,
      expectedCount: criteria.length,
    }
  }

  const goalReviewProps = computed(() => paneProps('goals'))
  const objectivesReviewProps = computed(() => paneProps('objectives'))

  function hasPaneActivity(pane: ReviewPaneProps): boolean {
    return isReviewing.value || Boolean(pane.error) || pane.results.length > 0
  }

  const isGoalReviewVisible = computed(() => !isDismissed.value && hasPaneActivity(goalReviewProps.value))
  const isObjectivesReviewVisible = computed(
    () => !isDismissed.value && hasObjectivesCriteria.value && hasPaneActivity(objectivesReviewProps.value)
  )

  function eventName(savedGoalEvent: string, goalEvent: string): string {
    return isSavedGoal ? savedGoalEvent : goalEvent
  }

  function openModal() {
    analytics.sendEvent(
      eventName(
        GOAL_AUTO_REVIEW_EVENT_NAMES.CLICKED_SAVED_AUTO_REVIEW_BUTTON,
        GOAL_AUTO_REVIEW_EVENT_NAMES.CLICKED_AUTO_REVIEW_BUTTON
      )
    )
    isModalOpen.value = true
  }

  function sendReceivedResults(results: AuditResult[]) {
    analytics.sendEvent(GOAL_AUTO_REVIEW_EVENT_NAMES.RECEIVED_RESULTS, {
      goals_audit_scores: resultsForSection(results, 'goals').map(toScoreSummary),
      objectives_audit_scores: resultsForSection(results, 'objectives').map(toScoreSummary),
      average_normalized_scores: calculateNormalizedScores(results),
      is_custom_criteria_set: isCustomCriteriaSet.value,
    })
  }

  function review() {
    analytics.sendEvent(
      eventName(GOAL_AUTO_REVIEW_EVENT_NAMES.CLICKED_SAVED_MODAL_REVIEW, GOAL_AUTO_REVIEW_EVENT_NAMES.CLICKED_MODAL_REVIEW)
    )
    isModalOpen.value = false
    isDismissed.value = false
    const { uuid, goalText, objectiveTexts } = toValue(goal)
    startReview(
      ({ signal, onProgress }) =>
        reviewGoalStream({
          goalUuid: uuid,
          goalText,
          objectiveTexts: [...objectiveTexts],
          contentProfile: contentReviewStore.reviewProfileId,
          betaMode: visibilityStore.betaVisibility,
          signal,
          onProgress,
        }),
      (results) => {
        hasCompletedReview.value = true
        sendReceivedResults(results)
      }
    )
  }

  function dismiss(dismissedEvent: string) {
    analytics.sendEvent(dismissedEvent)
    isDismissed.value = true
    cancelReview()
  }

  function dismissGoalReview() {
    dismiss(
      eventName(GOAL_AUTO_REVIEW_EVENT_NAMES.DISMISSED_SAVED_GOAL_RESULTS, GOAL_AUTO_REVIEW_EVENT_NAMES.DISMISSED_GOAL_RESULTS)
    )
  }

  function dismissObjectivesReview() {
    dismiss(
      eventName(
        GOAL_AUTO_REVIEW_EVENT_NAMES.DISMISSED_SAVED_OBJECTIVES_RESULTS,
        GOAL_AUTO_REVIEW_EVENT_NAMES.DISMISSED_OBJECTIVES_RESULTS
      )
    )
  }

  function trackResultToggle(result: AuditResult, context: AnalyticsData = {}) {
    analytics.sendEvent(GOAL_AUTO_REVIEW_EVENT_NAMES.CLICKED_RESULTS_ITEM_TOGGLE, {
      ...context,
      section: result.section,
      criteria_title: result.audit_title,
      score: result.score,
      max_score: result.max_score,
    })
  }

  function resetReview() {
    cancelReview()
    clearResults()
    isDismissed.value = false
    hasCompletedReview.value = false
  }

  watch(() => toValue(goal).uuid, resetReview)
  watch(() => contentReviewStore.reviewProfileId, resetReview)
  onScopeDispose(cancelReview)

  return {
    isModalOpen,
    isReviewAvailable,
    hasCompletedReview,
    criteriaSetName,
    criteriaByType,
    isCriterionVisible,
    reviewButtonText,
    reviewDescriptionLabel,
    goalReviewProps,
    objectivesReviewProps,
    isGoalReviewVisible,
    isObjectivesReviewVisible,
    openModal,
    review,
    dismissGoalReview,
    dismissObjectivesReview,
    trackResultToggle,
  }
}
